import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { ColumnDef } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import TableSkeleton from '../../components/TableSkeleton';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { useUrlFilters, COR_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import { useUserRole } from '../../utils/useUserRole';
import {
  Cor,
  CorSummary,
  corColumns,
  COR_TYPE_OPTIONS,
  COR_STATUS_OPTIONS,
  COR_RATING_BANDS,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
} from './config';
import AddCorModal from './components/AddCorModal';
import CorDetailModal from './components/CorDetailModal';
import { badge, ratingBadge } from './badges';
import './styles.scss';


/** "Nicos Terpkos" -> "NT"; a single word gives its first two letters. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() ?? '';
};

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

const CorRegister: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();
  const { isReadOnly } = useUserRole();

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCor, setSelectedCor] = useState<Cor | null>(null);

  const {
    filters: urlFilters,
    setFilter,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters,
    hasActiveFilters,
  } = useUrlFilters(COR_FILTER_DEFINITIONS);

  const searchInput = urlFilters.search;
  const filterStatus = urlFilters.status;
  const filterType = urlFilters.type;
  const filterRating = urlFilters.rating;
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;

  const {
    data: cors,
    pagination,
    summary,
    loading,
    filtering,
    error,
    setFilters,
    refetch,
  } = useServerFiltering<Cor>({
    rpcFunction: 'get_filtered_cors',
    defaultFilters: {
      search_term: searchInput || null,
      status_filter: filterStatus !== 'All' ? [filterStatus] : null,
      type_filter: filterType.length > 0 ? filterType : null,
      rating_filter: filterRating.length > 0 ? filterRating : null,
      page_size: urlPageSize || DEFAULT_PAGE_SIZE,
      page_offset: ((urlPage || 1) - 1) * (urlPageSize || DEFAULT_PAGE_SIZE),
      sort_by: 'reference_seq',
      sort_order: 'desc',
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY,
  });

  // Sync URL filters -> server filters
  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    setFilters({
      search_term: searchInput || null,
      status_filter: filterStatus !== 'All' ? [filterStatus] : null,
      type_filter: filterType.length > 0 ? filterType : null,
      rating_filter: filterRating.length > 0 ? filterRating : null,
      page_size: pageSize,
      page_offset: ((urlPage || 1) - 1) * pageSize,
    });
  }, [searchInput, filterStatus, filterType, filterRating, urlPage, urlPageSize, setFilters]);

  // Fetch management-role team members (managers + QA) for Raised By / Owner
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!session?.user) return;
      const { data, error: tmError } = await supabase
        .from('team_members')
        .select('id, first_name, last_name')
        .eq('is_active', true)
        .in('role', ['manager', 'qa'])
        .order('first_name', { ascending: true });
      if (tmError) {
        console.error('Error fetching team members:', tmError);
      } else {
        setTeamMembers((data as TeamMember[]) || []);
      }
    };
    fetchTeamMembers();
  }, [session, supabase]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleStatusChange = useCallback((status: string) => {
    setUrlFilters({ status, page: 1 });
  }, [setUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const isNarrow = useNarrowViewport();

  const handleTypeChange = useCallback((types: string[]) => {
    setUrlFilters({ type: types, page: 1 });
  }, [setUrlFilters]);

  const handleRatingChange = useCallback((ratings: string[]) => {
    setUrlFilters({ rating: ratings, page: 1 });
  }, [setUrlFilters]);

  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  const handleRowClick = useCallback((id: number) => {
    const cor = cors.find(c => c.id === id);
    if (cor) {
      setSelectedCor(cor);
      setIsDetailModalOpen(true);
    }
  }, [cors]);

  const handleCorCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleCorChanged = useCallback(() => {
    refetch();
    setIsDetailModalOpen(false);
    setSelectedCor(null);
  }, [refetch]);

  // Status pill-radio options with counts (counts ignore the status filter itself)
  const corSummary = summary as CorSummary | null;
  const statusCountByValue = useMemo<Record<string, number>>(() => ({
    'Open': corSummary?.open_count || 0,
    'In Progress': corSummary?.in_progress_count || 0,
    'Closed': corSummary?.closed_count || 0,
    'Monitoring': corSummary?.monitoring_count || 0,
  }), [corSummary]);

  const statusOptions = useMemo(() => [
    { value: 'All', label: 'All', count: corSummary?.total_count || 0 },
    ...COR_STATUS_OPTIONS.map(s => ({ value: s, label: s, count: statusCountByValue[s] })),
  ], [corSummary, statusCountByValue]);

  const typeItems: DropdownItem[] = COR_TYPE_OPTIONS.map(s => ({ id: s, label: s, value: s }));
  const ratingItems: DropdownItem[] = COR_RATING_BANDS.map(s => ({ id: s, label: s, value: s }));

  const columns = useMemo<ColumnDef<Cor>[]>(() => {
    // Column budget (table-layout: fixed; see the Documents register). Ten
    // columns, so the page uses the shell's --tight density; the fixed
    // columns come to ~1010px and Title takes the remainder.
    const columnConfig: Record<string, { minWidth?: string; width?: string; maxWidth?: string }> = {
      reference: { minWidth: '96px', width: '96px' },
      type: { minWidth: '116px', width: '116px' },
      date_raised: { minWidth: '112px', width: '112px' },
      title: { minWidth: '200px', width: 'auto' },
      initial_rating: { minWidth: '124px', width: '124px' },
      management_approach: { minWidth: '124px', width: '124px' },
      residual_rating: { minWidth: '124px', width: '124px' },
      owner: { minWidth: '96px', width: '96px' },
      status: { minWidth: '124px', width: '124px' },
      target_date: { minWidth: '128px', width: '128px' }, // last column: the 24px right gutter
    };

    return Object.entries(corColumns).map(([key, config]) => ({
      accessorKey: key,
      header: config.header,
      enableSorting: config.sortable ?? false,
      meta: columnConfig[key],
      cell: ({ row }) => {
        const cor = row.original;
        switch (config.component) {
          case 'TypeBadge':
            return badge(cor.type);
          case 'StatusBadge':
            return badge(cor.status);
          case 'RatingBadge':
            return ratingBadge(cor.initial_band, cor.initial_rating);
          case 'ResidualRatingBadge':
            return ratingBadge(cor.residual_band, cor.residual_rating);
          case 'ApproachTag':
            return cor.management_approach
              ? <span className="ui-chip">{cor.management_approach}</span>
              : <span className="text-muted">—</span>;
          case 'DateColumn': {
            const dateVal = row.getValue(key) as string | null;
            return <span>{dateVal ? format(parseISO(dateVal), 'd MMM yyyy') : '—'}</span>;
          }
          case 'TitleColumn':
            return cor.title
              ? <span className="cor-title-cell" title={cor.title}>{cor.title}</span>
              : <span className="text-muted">—</span>;
          case 'OwnerColumn': {
            if (!cor.owner) return <span className="text-muted">—</span>;
            const name = `${cor.owner.first_name} ${cor.owner.last_name}`;
            return <span className="ui-avatar" title={name} aria-label={name}>{initials(name)}</span>;
          }
          default: {
            const value = row.getValue(key);
            return <span>{(value as string) || '—'}</span>;
          }
        }
      },
    }));
  }, []);

  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  return (
    <ListPage
      title="COR Register"
      className="cor-page list-page--tight"
      skeleton={{ segmentCount: 5, filterCount: 2, showAction: true }}
      search={
        <SearchInput
          placeholder="Search COR items, titles, descriptions…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search COR items"
        />
      }
      actions={
        <button
          type="button"
          className="pill-btn pill-btn--solid pill-btn--sm"
          onClick={() => setIsAddModalOpen(true)}
          disabled={isReadOnly}
          title={isReadOnly ? 'Managers only' : undefined}
        >
          <PlusIcon /> Add item
        </button>
      }
      segments={
        <StatusSegments
          aria-label="Status"
          size={isNarrow ? 'sm' : 'md'}
          value={filterStatus}
          onChange={handleStatusChange}
          segments={statusOptions.map((o) => ({ value: o.value, label: o.label, count: o.count }))}
        />
      }
      filters={
        <>
          <FilterTrigger label="Type" active={filterType.length > 0}>
            <Dropdown items={typeItems} value={filterType} onChange={handleTypeChange} placeholder="All" multiSelect searchable={false} />
          </FilterTrigger>
          <FilterTrigger label="Rating" active={filterRating.length > 0}>
            <Dropdown items={ratingItems} value={filterRating} onChange={handleRatingChange} placeholder="All" multiSelect searchable={false} />
          </FilterTrigger>
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={resetUrlFilters}
      resultsText={
        !loading && pagination && (
          <>
            Showing {pagination.total_records === 0 ? 0 : ((currentPage - 1) * currentPageSize) + 1}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} items
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {error && (
        <div className="list-page__error">
          Error loading COR items: {typeof error === 'string' ? error : (error as any)?.message || 'Unknown error'}
        </div>
      )}

      {loading ? (
        <TableSkeleton compact rows={14} columns={10} showHeader />
      ) : cors.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters
            ? 'No COR items match these filters.'
            : 'No COR items yet. Create your first Change / Opportunity / Risk to get started.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table data={cors} columns={columns} onRowClick={(row) => handleRowClick(row.id)} stickyHeader />
        </div>
      )}

      <AddCorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCorCreated={handleCorCreated}
        teamMembers={teamMembers}
      />

      <CorDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedCor(null);
        }}
        cor={selectedCor}
        onCorUpdated={handleCorChanged}
        onCorDeleted={handleCorChanged}
        teamMembers={teamMembers}
        readOnly={isReadOnly}
      />
    </ListPage>
  );
};

export default CorRegister;
